'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Wand2, ChevronDown, Eye, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  listAttendanceTemplates,
  applyAttendanceTemplate,
  type AttendanceTemplateDTO,
  type AttendanceTemplateContent,
} from '@/services/attendanceTemplates';

interface AttendanceMaskSelectorProps {
  /** Conteúdo atual dos campos — usado para detectar sobrescrita */
  currentContent?: AttendanceTemplateContent;
  /** Callback chamado quando o médico confirma a aplicação da máscara */
  onApply: (content: AttendanceTemplateContent) => void;
  /** Desabilita o seletor (ex: prontuário finalizado) */
  disabled?: boolean;
}

/**
 * AttendanceMaskSelector
 *
 * Botão + modal para seleção e aplicação de máscaras de atendimento.
 *
 * Fluxo:
 * 1. Médico clica em "Aplicar Máscara" → abre modal com lista
 * 2. Seleciona uma máscara → vê preview dos blocos
 * 3. Se campos já preenchidos → diálogo de confirmação de sobrescrita
 * 4. Confirma → callback onApply(content) é invocado
 */
export function AttendanceMaskSelector({
  currentContent,
  onApply,
  disabled = false,
}: AttendanceMaskSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [templates, setTemplates] = useState<AttendanceTemplateDTO[]>([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AttendanceTemplateDTO | null>(null);
  const [pendingContent, setPendingContent] = useState<AttendanceTemplateContent | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const res = await listAttendanceTemplates();
      if (res.success && res.data) {
        setTemplates(res.data);
      }
    } catch {
      toast.error('Erro ao carregar máscaras');
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) fetchTemplates();
  }, [isOpen, fetchTemplates]);

  /** Verifica se algum campo de conteúdo já está preenchido */
  const hasExistingContent = useCallback((): boolean => {
    if (!currentContent) return false;
    return Object.values(currentContent).some((v) => {
      if (typeof v === 'string') return v.trim().length > 0;
      if (typeof v === 'object' && v !== null) {
        if ('predefined' in v) return (v as { predefined: string[] }).predefined.length > 0;
        return Object.values(v).some((inner) => inner && String(inner).trim().length > 0);
      }
      return false;
    });
  }, [currentContent]);

  const handleApplyClick = async (template: AttendanceTemplateDTO) => {
    setIsApplying(true);
    try {
      const res = await applyAttendanceTemplate(template.id);
      if (!res.success || !res.data) {
        toast.error(res.error ?? 'Erro ao aplicar máscara');
        return;
      }

      const content = res.data.content;

      if (hasExistingContent()) {
        // Há conteúdo preenchido → pede confirmação
        setSelectedTemplate(template);
        setPendingContent(content);
        setIsOpen(false);
        setIsConfirmOpen(true);
      } else {
        // Campos vazios → aplica diretamente
        onApply(content);
        setIsOpen(false);
        toast.success(`Máscara "${template.name}" aplicada!`);
      }
    } catch {
      toast.error('Erro ao aplicar máscara');
    } finally {
      setIsApplying(false);
    }
  };

  const handleConfirmOverwrite = () => {
    if (pendingContent) {
      onApply(pendingContent);
      toast.success(`Máscara "${selectedTemplate?.name}" aplicada!`);
    }
    setIsConfirmOpen(false);
    setPendingContent(null);
    setSelectedTemplate(null);
  };

  const handleCancelOverwrite = () => {
    setIsConfirmOpen(false);
    setPendingContent(null);
    setSelectedTemplate(null);
  };

  const blockLabels: Partial<Record<keyof AttendanceTemplateContent, string>> = {
    chiefComplaint: 'Queixas',
    hda: 'HDA',
    physicalExam: 'Exame físico',
    hypothesis: 'Hipótese',
    conduct: 'Conduta',
    observations: 'Observações',
    relatedDiseases: 'Doenças relacionadas',
    suggestedMedications: 'Medicamentos sugeridos',
  };

  const getFilledBlocks = (content: AttendanceTemplateContent): string[] => {
    const editorKeys: Array<keyof AttendanceTemplateContent> = [
      'chiefComplaint',
      'hda',
      'physicalExam',
      'hypothesis',
      'conduct',
      'observations',
      'relatedDiseases',
      'suggestedMedications',
    ];

    return editorKeys.filter((key) => {
      const v = content[key];
      if (typeof v === 'string') return v.trim().length > 0;
      if (Array.isArray(v)) return v.length > 0;
      return false;
    });
  };

  return (
    <>
      {/* ── Botão trigger ── */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => setIsOpen(true)}
        className="gap-2 border-teal-200 text-teal-700 hover:bg-teal-50 hover:border-teal-400 dark:border-teal-800 dark:text-teal-400 dark:hover:bg-teal-950"
      >
        <Wand2 className="w-4 h-4" />
        Aplicar Máscara
        <ChevronDown className="w-3 h-3 opacity-60" />
      </Button>

      {/* ── Modal de seleção ── */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-teal-600" />
              Selecionar Máscara de Atendimento
            </DialogTitle>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto space-y-3 py-2 pr-1">
            {isLoadingList ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Wand2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Nenhuma máscara cadastrada</p>
                <p className="text-sm mt-1">
                  Crie suas máscaras em{' '}
                  <a href="/mascaras" className="text-teal-600 underline">
                    Máscaras de Atendimento
                  </a>
                </p>
              </div>
            ) : (
              templates.map((template) => {
                const filledBlocks = getFilledBlocks(template.content);
                return (
                  <div
                    key={template.id}
                    className="group border border-gray-200 dark:border-neutral-700 rounded-xl p-4 hover:border-teal-300 dark:hover:border-teal-600 transition-colors cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {template.isFeatured && (
                            <span className="text-amber-500 text-xs font-bold">★</span>
                          )}
                          <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                            {template.name}
                          </h4>
                          {template.category && (
                            <Badge variant="outline" className="text-xs shrink-0">
                              {template.category}
                            </Badge>
                          )}
                        </div>
                        {filledBlocks.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {filledBlocks.map((block) => (
                              <span
                                key={block}
                                className="inline-flex items-center gap-1 text-[10px] font-medium bg-teal-50 text-teal-700 dark:bg-teal-950 dark:text-teal-300 px-2 py-0.5 rounded-full"
                              >
                                <Check className="w-2.5 h-2.5" />
                                {blockLabels[block as keyof AttendanceTemplateContent] ?? block}
                              </span>
                            ))}
                          </div>
                        )}
                        {template.usageCount > 0 && (
                          <p className="text-[10px] text-gray-400 mt-2">
                            Usado {template.usageCount}x
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        disabled={isApplying}
                        onClick={() => handleApplyClick(template)}
                        className="bg-teal-600 hover:bg-teal-700 text-white shrink-0 gap-1"
                      >
                        {isApplying ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                        Aplicar
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Diálogo de confirmação de sobrescrita ── */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Sobrescrever campos preenchidos?
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 text-sm text-gray-600 dark:text-gray-400">
            <p>
              Alguns campos já foram preenchidos. Ao aplicar a máscara{' '}
              <strong>"{selectedTemplate?.name}"</strong>, o conteúdo atual será substituído.
            </p>
            <p className="mt-2 font-medium text-gray-700 dark:text-gray-300">
              Deseja continuar?
            </p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelOverwrite}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmOverwrite}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              Sim, substituir conteúdo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
